import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import type { Me, Profile } from "@/lib/types";
import { LANG, langFromLocale, localeFromLang, setLang, t } from "@shared/i18n";

export const meKey = ["me"] as const;

export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async () => (await api<{ data: Me }>("/api/me")).data,
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
    staleTime: 30_000,
  });
}

export type ProfilePatch = Partial<Omit<Profile, "userId" | "onboardingCompletedAt" | "aiConsentAt" | "marketingOptInAt">> & { name?: string; aiConsent?: boolean; marketingOptIn?: boolean };

export function useUpdateProfile(opts: { silent?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProfilePatch) => api("/api/me/profile", { method: "PATCH", json: patch }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meKey });
      if (!opts.silent) toast.success(t("Préférences enregistrées."));
    },
    onError: (e) => toast.error(e.message),
  });
}

/** Aligne la langue de l'interface sur le profil ; à l'inscription, enregistre la langue en cours dans le profil. */
export function useLanguageSync(me: Me | undefined) {
  const { mutate } = useUpdateProfile({ silent: true });
  const profileLocale = me?.profile.locale;
  const onboarded = Boolean(me?.profile.onboardingCompletedAt);
  useEffect(() => {
    if (!profileLocale) return;
    const profileLang = langFromLocale(profileLocale);
    if (profileLang === LANG) return;
    if (onboarded) setLang(profileLang);
    else mutate({ locale: localeFromLang(LANG) });
  }, [profileLocale, onboarded, mutate]);
}
